// Document hero component for document pages.

import React from 'react';
import { ASSETS } from '@airhead-games/shared/configs';
import {
  getReportGradientAccentFamily,
  type Document,
} from '@airhead-games/shared/content';
import BackgroundBlurred from '@/content/rendering/chrome/BackgroundBlurred';
import BackgroundGradient from '@/content/rendering/chrome/BackgroundGradient';
import { getDocumentHeroIcon } from '@/content/rendering/chrome/heroIcons';
import RichTextRenderer from '@/content/rendering/renderers/RichTextRenderer';
import { cn } from '@/utilities/cn';
import { getColorStop } from '@/styles/playerColors';

interface DocumentHeroProps {
  document: Document;
}

const getReportAccent = (document: Document) => {
  const family = getReportGradientAccentFamily(document.meta?.reportMonth);
  return getColorStop(family, '500');
};

const DocumentHero: React.FC<DocumentHeroProps> = ({ document }) => {
  const hero = document.hero;
  const imageAsset = hero?.imageAsset;
  const showDescription = Boolean(hero?.showDescription);
  const isReport = document.kind === 'report';
  const reportAccent = isReport ? getReportAccent(document) : undefined;
  const useBlurredImageBackground = Boolean(
    hero?.background === 'blurred-image' && imageAsset
  );
  const useGradientCardBackground = hero?.background === 'gradient-card';
  const useReportBackground = isReport && !useGradientCardBackground;
  const usePaddedHero =
    useBlurredImageBackground || useGradientCardBackground || isReport;
  const useLightHeroText = useGradientCardBackground || isReport;
  const HeroIcon = useGradientCardBackground
    ? getDocumentHeroIcon(document)
    : undefined;
  const showImage = Boolean(imageAsset && !HeroIcon);

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg',
        useGradientCardBackground && 'ring-1 ring-white/20'
      )}
      style={
        useReportBackground && reportAccent
          ? ({
              '--report-accent': reportAccent,
            } as React.CSSProperties & Record<'--report-accent', string>)
          : undefined
      }
    >
      {useReportBackground && reportAccent && (
        <>
          <div
            className="absolute inset-0"
            style={{
              backgroundColor:
                'color-mix(in oklab, var(--report-accent) 12%, #09090b)',
              backgroundImage: `
                linear-gradient(180deg, rgba(9, 9, 11, 0.2), rgba(9, 9, 11, 0.72)),
                radial-gradient(circle at 16% 14%, color-mix(in oklab, var(--report-accent) 44%, #09090b), transparent 46%),
                radial-gradient(circle at 84% 82%, color-mix(in oklab, var(--report-accent) 28%, #09090b), transparent 40%),
                linear-gradient(
                  135deg,
                  color-mix(in oklab, var(--report-accent) 30%, #111827) 0%,
                  color-mix(in oklab, var(--report-accent) 18%, #09090b) 55%,
                  #09090b 100%
                )
              `,
            }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 bg-black/20" aria-hidden="true" />
        </>
      )}
      {useGradientCardBackground && (
        <BackgroundGradient family={hero?.backgroundAccentFamily ?? 'lime'} />
      )}
      {useBlurredImageBackground && imageAsset && (
        <BackgroundBlurred imageAsset={imageAsset} />
      )}
      {HeroIcon && (
        <HeroIcon
          aria-hidden="true"
          strokeWidth={1.75}
          className="pointer-events-none absolute top-5 right-5 z-10 size-9 text-white sm:top-6 sm:right-6 sm:size-10 md:top-8 md:right-8 md:size-12 lg:top-10 lg:right-10 lg:size-14"
        />
      )}
      <div
        className={cn(
          'relative grid gap-6 md:gap-8',
          showImage &&
            'xl:grid-cols-[minmax(0,1fr)_minmax(18rem,26rem)] xl:items-center 2xl:grid-cols-[minmax(0,1fr)_minmax(22rem,32rem)]'
        )}
      >
        <div
          className={cn(
            usePaddedHero
              ? 'flex flex-col gap-4 p-5 sm:p-6 md:p-8 lg:p-10'
              : 'flex flex-col gap-4',
            HeroIcon && 'pr-16 sm:pr-20 md:pr-24 lg:pr-28'
          )}
        >
          <h1
            className={
              useLightHeroText
                ? 'font-heading text-4xl leading-tight font-semibold tracking-tight text-white sm:text-5xl lg:text-5xl xl:text-6xl'
                : 'font-heading text-4xl leading-tight font-semibold tracking-tight sm:text-5xl lg:text-5xl xl:text-6xl'
            }
          >
            <RichTextRenderer
              nodes={
                hero?.title ?? [{ type: 'text', text: document.seo.title }]
              }
            />
          </h1>
          {showDescription && hero?.description && (
            <p
              className={
                useLightHeroText
                  ? 'max-w-4xl text-base leading-6 text-white/80 sm:text-lg sm:leading-7 xl:text-xl xl:leading-8'
                  : 'max-w-4xl text-base leading-6 text-muted-foreground sm:text-lg sm:leading-7 xl:text-xl xl:leading-8'
              }
            >
              <RichTextRenderer nodes={hero.description} />
            </p>
          )}
        </div>
        {showImage && imageAsset && (
          <div
            className={
              usePaddedHero
                ? 'p-5 pt-0 sm:p-6 sm:pt-0 md:p-8 md:pt-0 xl:p-6 2xl:p-8'
                : undefined
            }
          >
            <img
              src={ASSETS[imageAsset]}
              alt={hero?.imageAlt ?? document.seo.title}
              className="aspect-[4/3] w-full rounded-lg border border-border bg-muted object-cover"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default DocumentHero;
